<script lang="ts" setup>
import type { CompaniesAsk } from '#components'

const props = defineProps({
  list: {
    default: undefined,
    type: Object as PropType<List>,
  },
})

const emit = defineEmits<{
  toggleCompaniesFromList: [{ action: 'attach' | 'detach', companies: CompanyV2[] }]
}>()

const { createEvent } = useAnalytics()
const { t } = useI18n()
const companiesStore = useCompaniesStore()
const guards = useGuards()
const localePath = useLocalePath()
const router = useRouter()
const { companyDomain, companyDomains, companyDomainsQuery, history, nextGroupCompany, previousGroupCompany, showCompanyGroup } = useCompany()
const { toggleSlideover } = useOverlays()
const companiesAskRef = useTemplateRef<InstanceType<typeof CompaniesAsk>>('companiesAskRef')

const activeTabSlug = useRouteQuery<string>('tab', 'company')
const loading = useState<boolean>('company-slideover-loading', () => true)
const loadingSimilars = useState<boolean>('company-slideover-loading-similars', () => true)
const company = useState<CompanyV2 | undefined>('company-slideover-company', () => undefined)

const companySimilars = ref<CompanyV2[]>([])
const prompt = ref('')
const selectedCompletion = ref({
  color: getFeatureColor('ask'),
  key: 'ask',
  label: 'ask',
})

const tabs = ref([
  {
    icon: getIcon('company'),
    label: t('toggle.company'),
    value: 'company',
  },
  {
    icon: getIcon('ask'),
    label: t('toggle.ask'),
    value: 'ask',
  },
  {
    icon: getIcon('code'),
    label: t('toggle.json'),
    value: 'json',
  },
])

const activeTab = computed({
  get: () => {
    const index = tabs.value.findIndex(tab => tab.value === activeTabSlug.value)

    if (index === -1) {
      return 0
    }

    return index
  },
  set: (index: number) => (activeTabSlug.value = tabs.value[index].value),
})

const isOpened = computed({
  get: () => !!companyDomain.value,
  set: (value: boolean) => {
    if (!value) {
      companyDomain.value = ''
    }
  },
})

const previousCompany = computed(() => {
  return history?.history?.value?.find(historyItem => historyItem.snapshot !== company.value?.domain?.domain)?.snapshot
})

onBeforeUnmount(() => {
  company.value = undefined
  history.clear()
})

watch(
  companyDomain,
  async (newDomain) => {
    if (!newDomain) {
      toggleSlideover({
        opened: false,
        type: 'company',
      })

      return nextTick(() => history.clear())
    }

    toggleSlideover({
      opened: true,
      type: 'company',
    })

    if (newDomain !== company.value?.domain?.domain) {
      const company = await fetchCompany()

      if (!company) {
        return companyDomain.value = ''
      }

      fetchSimilarCompanies()

      if (company.domain?.domain && !history.history.value.find(historyItem => historyItem.snapshot === company.domain?.domain)) {
        history.commit()
      }

      createEvent({ name: '[Navigation] Visit Company Profile' })
    }
  },
  { immediate: true },
)

async function fetchCompany(domain?: string) {
  if (import.meta.client) {
    document.querySelector('.js-slideover-body')?.scrollTo({
      behavior: 'smooth',
      left: 0,
      top: 0,
    })
  }

  loading.value = true

  if (guards.userOnly() && (domain || companyDomain.value)) {
    try {
      // Fetch the company profile from list
      if (props.list) {
        company.value = await companiesStore.fetchCompany({
          domain: domain || companyDomain.value,
          listId: props.list.id,
        })
      }
      // Fetch the company profile if credits available
      else {
        company.value = await companiesStore.fetchCompany({ domain: domain || companyDomain.value })
      }
    }
    catch (error) {
      const axiosError = error as AxiosError
      const axiosErrorData = axiosError.response?.data as { message?: string }

      if (axiosError.response?.status === 404) {
        router.push(localePath('404'))
      }
      else if (axiosErrorData?.message === 'noCreditsRemaining') {
        router.push(localePath('settings-subscription'))
      }
      else {
        console.error(error)
      }
    }
  }

  loading.value = false

  return company.value
}

async function fetchSimilarCompanies(domain?: string) {
  loadingSimilars.value = true

  try {
    const similarsResponse = await companiesStore.fetchCompanies({
      similarDomains: [domain || companyDomain.value],
      simplified: true,
    })

    if (similarsResponse?.companies?.length) {
      companySimilars.value = similarsResponse.companies
    }
  }
  catch {}

  loadingSimilars.value = false
}

function onCompanyProfileClosed() {
  companyDomain.value = ''
  companyDomainsQuery.value = ''
  activeTabSlug.value = 'company'
}

function onLoadCompany(domain: string, fromHistory: boolean = false) {
  if (fromHistory) {
    companyDomain.value = previousCompany.value!

    history.undo()

    return
  }

  companyDomain.value = domain
}

useEvent(
  'global',
  'metrics:update',
  ({ domains }) => {
    if (domains.includes(companyDomain.value)) {
      fetchCompany()
    }
  },
)
</script>

<template>
  <UDashboardSlideover
    v-model="isOpened"
    class="z-30"
    :ui="{
      header: {
        base: 'hidden',
        padding: 'p-0',
      },
      body: {
        base: 'flex-1 overflow-y-auto js-slideover-body z-90',
        padding: 'p-0',
      },
      footer: {
        base: 'p-O border-t border-gray-200 dark:border-gray-800',
        padding: 'p-0',
      },
    }"
    @close="onCompanyProfileClosed"
  >
    <div class="gradient-texture-reverse relative flex w-full flex-col items-center">
      <div class="relative flex w-full items-center justify-between p-2">
        <div class="flex items-center">
          <UButtonGroup
            v-if="companyDomains.length && (companyDomains.findIndex(domain => domain === companyDomain) + 1) > 0"
            size="2xs"
          >
            <UButton
              class="w-32"
              size="xs"
              @click="showCompanyGroup(companyDomains, previousGroupCompany)"
            >
              <div class="flex w-full items-center justify-between gap-2 overflow-hidden">
                <UIcon
                  class="size-4"
                  :name="getIcon('arrowLeft')"
                />

                <span class="truncate">
                  {{ previousGroupCompany }}
                </span>
              </div>
            </UButton>

            <UButton
              class="w-12"
              disabled
              size="xs"
            >
              <div class="flex w-full items-center justify-center">
                <span class="truncate">
                  {{ (companyDomains.findIndex(domain => domain === companyDomain) + 1) || 1 }}/{{ companyDomains.length }}
                </span>
              </div>
            </UButton>

            <UButton
              class="w-32"
              size="xs"
              @click="showCompanyGroup(companyDomains, nextGroupCompany)"
            >
              <div class="flex w-full items-center justify-between gap-2 overflow-hidden">
                <span class="truncate">
                  {{ nextGroupCompany }}
                </span>

                <UIcon
                  class="size-4"
                  :name="getIcon('arrowRight')"
                />
              </div>
            </UButton>
          </UButtonGroup>

          <UButton
            v-else-if="!companyDomains.length && previousCompany && previousCompany !== company?.domain?.domain && !companyDomains.length"
            class="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800"
            :icon="getIcon('back')"
            :label="`${previousCompany}`"
            size="xs"
            @click="onLoadCompany(previousCompany, true)"
          />
        </div>

        <UButton
          class="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800"
          :icon="getIcon('close')"
          size="xs"
          @click="onCompanyProfileClosed"
        />
      </div>

      <CompanyCard
        class="-mt-2 p-4"
        :company="company"
      />

      <UTabs
        v-model="activeTab"
        class="-mt-2 mb-1 w-full space-y-0 !rounded-none px-3"
        :items="tabs"
        size="sm"
        :ui="{
          container: '!rounded-none',
          base: '!rounded-none',
          wrapper: '!rounded-none',
          list: {
            base: '!rounded-none !bg-transparent',
            tab: {
              font: 'hover:text-gray-900 dark:hover:text-gray-100',
            },
          },
        }"
        value
        variant="underlined"
      />
    </div>

    <CompanyProfile
      v-if="activeTabSlug === 'company' && company"
      :company="company"
      has-close
      :loading="loading"
      :similar="companySimilars"
      @close="onCompanyProfileClosed"
      @load-company="onLoadCompany"
    />

    <div
      v-else-if="activeTabSlug === 'json'"
      class="border-t border-gray-200 p-4 dark:border-gray-800"
    >
      <ApiResponse
        :has-header="false"
        :level-to-open="4"
        :loading="loading"
        :response="company"
      />
    </div>

    <div
      v-else-if="activeTabSlug === 'ask'"
      class="border-t border-gray-200 p-4 dark:border-gray-800"
    >
      <CompaniesAsk
        v-if="company"
        ref="companiesAskRef"
        v-model:prompt="prompt"
        :company="company"
      />
    </div>

    <template #footer>
      <div class="gradient-texture-reverse h-18 w-full p-4">
        <div
          v-if="activeTabSlug === 'ask' && company?.domain?.domain"
          class="flex h-full items-center"
        >
          <Prompt
            v-model="prompt"
            v-model:completion="selectedCompletion"
            class="z-20"
            force-button
            :force-cleanup="false"
            :hoverable="false"
            light
            :loading="companiesAskRef?.loading"
            :loading-state="companiesAskRef?.currentLoadingState"
            :placeholders="[t('placeholders.ask1'), t('placeholders.ask2'), t('placeholders.ask3')]"
            show-content-on-loading
            @submit="companiesAskRef?.submitPrompt"
          />
        </div>

        <CompanyListsSelector
          v-else-if="company?.domain?.domain"
          class="w-full"
          :company="company"
          @toggle-companies="emit('toggleCompaniesFromList', $event)"
          @updated="fetchCompany"
        />
      </div>
    </template>
  </UDashboardSlideover>
</template>

<i18n lang="json">
{
  "en": {
    "placeholders": {
      "ask1": "Who is the founder and is he still the CEO?",
      "ask2": "What was the size of the company before 2020?",
      "ask3": "Is the company bootstrapped and/or profitable?"
    },
    "toggle": {
      "ask": "Ask",
      "company": "Company profile",
      "json": "JSON profile"
    }
  },
  "es": {
    "placeholders": {
      "ask1": "¿Quién es el fundador y sigue siendo el CEO?",
      "ask2": "¿Cuál era el tamaño de la empresa antes de 2020?",
      "ask3": "¿La empresa está autofinanciada y/o es rentable?"
    },
    "toggle": {
      "ask": "Preguntar",
      "company": "Perfil de empresa",
      "json": "Perfil JSON"
    }
  },
  "fr": {
    "placeholders": {
      "ask1": "Qui est le fondateur et est-il toujours le PDG?",
      "ask2": "Quelle était la taille de l'entreprise avant 2020?",
      "ask3": "L'entreprise est-elle autofinancée et/ou rentable?"
    },
    "toggle": {
      "ask": "Poser une question",
      "company": "Profil de l'entreprise",
      "json": "Profil JSON"
    }
  }
}
</i18n>
